import { Component, Vue } from 'vue-property-decorator'
import { Action, Getter, Mutation } from 'vuex-class'
import moment from 'moment-timezone'

import ApiRequest from '@/core/ApiRequest'
import RoomModel from '@/core/models/Room'
import { EventStatus } from '@/core/models/Event'
import { FETCH_HOLIDAYS, FETCH_ROOMS_LIST } from '@/store/root/actions'
import { GET_API_REQUEST, GET_ROOMS_LIST, GET_CALENDAR_LAST_DATE } from '@/store/root/getters'
import { SET_API_REQUEST, SET_CALENDAR_LAST_DATE } from '@/store/root/mutations'

@Component
export default class Calendar extends Vue {
  public startShownDate: any = null
  public endShownDate: any = null

  public mode: string = 'stack'
  public modes: Array<string> = ['stack', 'column']

  public value: string = ''
  public events: Array<any> = []

  public maxEventsInDay: number = 0
  public weeksCount: number = 5
  public readonly minEventsInDay: number = 2
  public readonly weekHeaderHeight: number = 20
  public readonly dayHeaderHeight: number = 40
  public readonly eventRowHeight: number = 23
  public readonly dayBottomPadding: number = 8

  public holidays: {[key: string]: string|null} = {}

  /**
   * Fetch holidays.
   */
  @Action(FETCH_HOLIDAYS)
  public fetchHolidays: (payload: { from: string, to: string }) => Array<any>

  /**
   * Fetch rooms list.
   */
  @Action(FETCH_ROOMS_LIST)
  public fetchRoomsList: () => void

  /**
   * Get calendar last date, based on global Vuex state.
   */
  @Getter(GET_CALENDAR_LAST_DATE)
  public getCalendarLastDate: String | null

  /**
   * Sets calendar last date instance.
   */
  @Mutation(SET_CALENDAR_LAST_DATE)
  public setCalendarLastDate: (lastDate: String | null) => void

  /**
   * Get rooms list, based on global Vuex state.
   */
  @Getter(GET_ROOMS_LIST)
  public getRoomsList: RoomModel[]

  /**
   * Get api request object, based on global Vuex state.
   */
  @Getter(GET_API_REQUEST)
  public getApiRequest: ApiRequest

  /**
   * Get events between start and end date.
   * @param start   Start date.
   * @param end     End date.
   */
  public async getEvents ({ start, end }: { start: any, end: any }): Promise<any> {
    const events = []
    let maxDiffRooms = 0

    // console.log(`= getEvents(${start.date}, ${end.date})`)
    // console.log('Start Week=' + moment(start.date).isoWeekday(1).format('YYYY-MM-DD'))
    // console.log('Start=' + moment(start.date).format('YYYY-MM-DD'))
    // console.log('End=' + moment(end.date).format('YYYY-MM-DD'))
    // console.log('End Week=' + moment(end.date).isoWeekday(7).format('YYYY-MM-DD'))
    const calendarFrom = moment(start.date).isoWeekday(1)
    const calendarTo = moment(end.date).isoWeekday(7)
    this.weeksCount = Math.ceil((calendarTo.diff(calendarFrom, 'days') + 1) / 7)

    const holidays = await this.fetchHolidays({
      from: calendarFrom.format('YYYY-MM-DD'),
      to: calendarTo.format('YYYY-MM-DD'),
    })
    this.holidays = {}
    if (holidays && holidays.length) {
      maxDiffRooms = 1
      for (const holiday of holidays) {
        this.holidays[holiday.day] = holiday.name
        events.push({
          name: '🌴 Выходной день',
          start: holiday.day,
          end: holiday.day,
          color: '#e980a3',
          timed: false,
        })
      }
    }
    this.setCalendarLastDate(start.date)
    this.startShownDate = start.date
    this.endShownDate = end.date

    const response = await this.getApiRequest.send(
      'GET',
      'events?date_from=' +
        encodeURIComponent((new Date(`${start.date}T00:00:00`)).toISOString()) +
        '&date_to=' +
        encodeURIComponent((new Date(`${end.date}T23:59:59`)).toISOString()),
    )
    if (!response || !response.data) {
      return []
    }

    const eventsByDates: {[key: string]: any} = {}
    for (const event of response.data as any) {
      const days = Calendar.getDaysArray(event.date_from, event.date_to)
      for (const day of days) {
        const dateKey: string = moment(day.toISOString()).format('YYYY-MM-DD')
        if (!eventsByDates[dateKey] || !eventsByDates[dateKey][event.room_id]) {
          const obj: any = !eventsByDates[dateKey] ? {} : eventsByDates[dateKey]
          obj[event.room_id] = [event]
          eventsByDates[dateKey] = obj
          continue
        }
        eventsByDates[dateKey][event.room_id].push(event)
      }
    }

    for (const dateKey in eventsByDates) {
      const diffRooms: Array<any> = []
      const dateEvents = eventsByDates[dateKey]

      if (this.holidays[dateKey] !== undefined) {
        diffRooms.push('_holiday')
      }


      for (const roomKey in dateEvents) {
        const roomEvents = dateEvents[roomKey]
        const notCanceledEvents = roomEvents.filter((e: any) => e.status !== 'canceled')
        const preorderedEvents = notCanceledEvents.filter((e: any) => e.status === EventStatus.PREORDERED)
        const count = roomEvents.length
        for (const ev of roomEvents) {
          if (!diffRooms.includes(ev.room_id)) {
            diffRooms.push(ev.room_id)
          }
        }
        const event = preorderedEvents.length > 0
          ? preorderedEvents[0]
          : (notCanceledEvents.length > 0 ? notCanceledEvents[0] : roomEvents[0])
        const hasPreorder = preorderedEvents.length > 0
        const hasOverduePreorder = preorderedEvents.some((e: any) => this.isPreorderDue(e))
        const name = event.room_name + (count > 1 ? ` (${count})` : '')
        const color = hasPreorder
          ? (hasOverduePreorder ? '#d84315' : '#f57c00')
          : (notCanceledEvents.length > 0 ? event.room_color : 'blue-grey lighten-4')
        const start = new Date(dateKey)
        start.setHours(0, 0, 0)
        const end = new Date(dateKey)
        end.setHours(23, 59, 59)
        const startFormatted = moment(start.toISOString()).format('YYYY-MM-DD')
        const endFormatted = moment(end.toISOString()).format('YYYY-MM-DD')

        let icons = ''
        const roomOptions = event.room_options?.length ? event.room_options.split(',') : []
        if (roomOptions.includes('Фуршет')) { // TODO: move to const, used in some parts
          icons = '🍽'
        }
        if (roomOptions.includes('Выездная церемония')) {
          icons = '🚚'
        }

        if (this.holidays[startFormatted] !== undefined) {
          icons = '⚠️'
        }
        if (event.type === 'Служебное: ремонт') {
          icons = '🔨'
        }

        if (icons) {
          icons += ' '
        }

        events.push({
          name: `${icons}${hasPreorder ? 'ПРЕДЗАКАЗ • ' : ''}${name}`,
          roomName: `${icons}${name}`,
          isPreordered: hasPreorder,
          isPreorderDue: hasOverduePreorder,
          statusConfirmationDate: event.status_confirmation_date,
          start: startFormatted,
          end: endFormatted,
          color,
          timed: false
        })
      }

      if (diffRooms.length > maxDiffRooms) { maxDiffRooms = diffRooms.length }
    }

    this.maxEventsInDay = Math.max(maxDiffRooms, this.minEventsInDay)

    this.events = events
    this.$nextTick(() => {
      this.setDayClickHandler()
    })
  }

  public get calendarHeight (): number {
    const eventRows = Math.max(this.maxEventsInDay, this.minEventsInDay)
    const weekHeight = this.dayHeaderHeight + eventRows * this.eventRowHeight + this.dayBottomPadding
    return this.weekHeaderHeight + this.weeksCount * weekHeight
  }

  public getEventColor (event: any): string {
    return event.color
  }

  public getCalendarEventClass (event: any): {[key: string]: boolean} {
    return {
      'calendar-event--preordered': !!event.isPreordered,
      'calendar-event--preorder-due': !!event.isPreorderDue,
    }
  }

  public getCalendarEventTitle (event: any): string {
    if (!event.isPreordered) {
      return event.name
    }
    const confirmation = this.getPreorderConfirmationText(event.statusConfirmationDate)
    return confirmation
      ? `${event.roomName}. Предзаказ, подтвердить до ${confirmation}`
      : `${event.roomName}. Предзаказ, дата подтверждения не указана`
  }

  public rnd (a: number, b: number): number {
    return Math.floor((b - a + 1) * Math.random()) + a
  }

  private static getDaysArray (start: string, end: string): Array<Date> {
    const dates = []
    const dt = new Date(start)
    dt.setHours(0, 0, 0, 0)
    const endDate = (new Date(end))
    endDate.setHours(23, 59, 59, 999)
    // eslint-disable-next-line no-unmodified-loop-condition
    for (; dt <= endDate; dt.setDate(dt.getDate() + 1)) {
      dates.push(new Date(dt))
    }
    return dates
  }

  public onDayClick (domEvent: any) {
    const target = (domEvent.target as any).closest('.v-calendar-weekly__day')
    const isOutside = target.classList.contains('v-outside')
    const element = target.querySelector('.v-calendar-weekly__day-label button.v-btn .v-btn__content')
    const dayNumber = element.textContent.replace(/\D/g, '')
    const date = new Date(this.startShownDate as any)
    date.setDate(dayNumber)
    if (isOutside) {
      date.setMonth(date.getMonth() + (dayNumber < 15 ? 1 : -1))
    }
    const resultDate = moment(date.toISOString()).format('YYYY-MM-DD')
    this.$router.push({ name: 'dayEvents', params: { date: resultDate } })
  }

  /**
   * Add onClick event handler for day element of calendar
   */
  public setDayClickHandler () {
    const elements = document.getElementsByClassName('v-calendar-weekly__day')
    for (const element of elements) {
      element.removeEventListener('click', this.onDayClick)
      element.addEventListener('click', this.onDayClick, true)
    }
  }

  public onTodayClick () {
    this.value = String(moment().format('YYYY-MM-DD'))
  }

  public isHoliday (date: string): boolean {
    return this.holidays[date] !== undefined
  }

  private isPreorderDue (event: any): boolean {
    if (event.status !== EventStatus.PREORDERED) {
      return false
    }
    if (!event.status_confirmation_date || isNaN(Date.parse(event.status_confirmation_date))) {
      return true
    }
    return moment(event.status_confirmation_date).isSameOrBefore(moment().endOf('day'))
  }

  private getPreorderConfirmationText (date: string | null): string | null {
    if (!date || isNaN(Date.parse(date))) {
      return null
    }
    return moment(date).format('DD.MM.YYYY')
  }

  /**
   * Created lifecycle hook. Fires when component instance is created.
   */
  public async created () {
    this.value = this.getCalendarLastDate
      ? String(this.getCalendarLastDate)
      : String(moment().format('YYYY-MM-DD'))
    await this.fetchRoomsList()
  }

  /**
   * VueJs mounted hook.
   */
  public mounted () {
    this.setDayClickHandler()
  }

  public getMonths () {
    const months = []
    for (let i = 0; i <= 11; i++) {
      months.push(moment().month(i).format('MMMM'))
    }
    return months
  }

  public getYears () {
    const years = []
    const nowYear = moment().format('YYYY')
    for (let i = +nowYear - 3; i <= +nowYear + 3; i++) {
      years.push(i)
    }
    return years
  }

  public getMonthName () {
    return moment(this.value).format('MMMM')
  }

  public getYearName () {
    return moment(this.value).format('YYYY')
  }

  public changeMonthTo (index: number): void {
    this.value = String(moment(this.value).month(index).format('YYYY-MM-DD'))
  }

  public changeYearTo (year: number): void {
    this.value = String(moment(this.value).year(year).format('YYYY-MM-DD'))
  }
}
